<script setup lang="ts">
import { dateFormatter } from '@/helpers/ep/formatters'
import { OrderBy } from '@/types/shared/filters'
import type { IRepairRequestSubmissionFilters } from '@/types/rc/filters'
import { Icon } from '@iconify/vue'
import { useRepairRequestSubmissions } from '@/composables/rc/repair-request-submissions'
import { reactive, ref } from 'vue'
import { RepairRequestSubmissionStatus } from '@/types/rc/repair-requests/RepairRequestSubmission'
import ViewLayout from '@/views/layouts/ViewLayout.vue'
import { useRouter } from 'vue-router'
import { ElMessageBox } from 'element-plus'
import type { IUpdateRepairSubmissionStatusForm } from '@/types/rc/forms'
import { DeleteConfirmationModal } from '@/components/common/DeleteConfirmationModal'
import { DEFAULT_PAGE_SIZES, DEFAULT_PAGINATION_LAYOUT } from '@/types/shared/pagination'
import RcCustomerSelect from '@/components/rc/common/RcCustomerSelect.vue'
import RepairRequestSubmissionDialog from '@/components/rc/repair-request-submissions/RepairRequestSubmissionDialog.vue'
import type { ICustomer } from '@/types/core/Customer'
import { useOIDCStore } from '@/stores/oidc.store'

const { isCustomer, isRcAppManagerUser } = useOIDCStore()

interface IRepairRequestTableRow {
  id: number
  repair_request?: any
  status: string
}

const router = useRouter()

const filters = reactive<IRepairRequestSubmissionFilters>({
  page: 1,
  per_page: DEFAULT_PAGE_SIZES[2],
  order_by: 'created_at',
  order_by_order: OrderBy.ASC,
  status: undefined,
})

const createRepairRequestSubmissionDialogOpen = ref<boolean>(false)

const {
  getRepairRequestSubmissionsQuery,
  deleteRepairRequestSubmissionMutation,
  updateRepairRequestSubmissionStatusMutation,
} = useRepairRequestSubmissions()

const { isFetching, isError, data, error, isRefetching, refetch } = getRepairRequestSubmissionsQuery(filters)

const getRowClassName = (data: { row: IRepairRequestTableRow }) => {
  const { row } = data
  return row.status === RepairRequestSubmissionStatus.Approved && isRcAppManagerUser
    ? 'repair-request-submissions-dashboard-table-row'
    : ''
}

const handleOpen = (row: IRepairRequestTableRow) => {
  if (row.repair_request && isRcAppManagerUser) {
    navigateToRepairRequest(row.repair_request)
  }
}

const navigateToRepairRequest = async (repairRequest: any) => {
  await router.push({
    name: 'rc-customer-repair-requests',
    params: { customerId: repairRequest.customer_id },
    query: { number: repairRequest.number },
  })
}

const navigateToWorkOrder = async (workOrderNumber: string) => {
  await router.push({
    name: 'rc-work-order-dashboard',
    params: { number: workOrderNumber },
  })
}

const handleCustomerSelectChange = (val: ICustomer | undefined) => {
  filters.customer_id = val?.id
}

const handleStatusApproved = async (submissionID: number) => {
  ElMessageBox.confirm(
    'Are you sure you want to approve this submission? This action cannot be undone',
    'Approve Submission',
    {
      confirmButtonText: 'Approve',
      cancelButtonText: 'Back',
      confirmButtonClass: 'el-button--success',
    }
  )
    .then(async () => {
      const updateForm: IUpdateRepairSubmissionStatusForm = {
        status: RepairRequestSubmissionStatus.Approved,
        reason: undefined,
      }
      await updateRepairRequestSubmissionStatusMutation.mutateAsync({ submissionID, updateForm })
    })
    .catch(() => {
      // ElMessageBox throws an Error on cancel click (in this case, do nothing).
    })
}

const handleStatusRejected = async (submissionID: number) => {
  ElMessageBox.prompt(
    'Provide a rejection reason for this submission. This action cannot be undone',
    'Reject Submission',
    {
      confirmButtonText: 'Reject',
      cancelButtonText: 'Back',
      inputPlaceholder: 'E.g: miner models not supported',
      confirmButtonClass: 'el-button--danger',
      inputType: 'textarea',
      inputValidator: (value) => {
        if (!value) {
          return 'Reject reason is required'
        }
        return true
      },
    }
  )
    .then(async (result) => {
      const updateForm: IUpdateRepairSubmissionStatusForm = {
        status: RepairRequestSubmissionStatus.Rejected,
        reason: result.value,
      }
      await updateRepairRequestSubmissionStatusMutation.mutateAsync({ submissionID, updateForm })
    })
    .catch(() => {
      // ElMessageBox throws an Error on cancel click (in this case, do nothing).
    })
}
</script>

<template>
  <ViewLayout>
    <template #controls>
      <el-button v-if="isCustomer" type="primary" @click="createRepairRequestSubmissionDialogOpen = true"
        >Create Submission</el-button
      >
    </template>
    <rl-error-result v-if="isError && !isRefetching" :error="error">
      <el-button @click="refetch()">
        <Icon icon="material-symbols:refresh-rounded" height="16" width="16" :inline="true" />&nbsp;Reload
      </el-button>
    </rl-error-result>
    <template v-else>
      <!-- Informational message for customers -->
      <div v-if="isCustomer" class="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-md">
        <div class="flex items-center">
          <Icon icon="material-symbols:info-rounded" class="text-blue-600 mr-2" />
          <div class="text-blue-800 text-sm">
            <p>
              You can submit your repair requests here. The repair center will review them as soon as possible and
              you'll be notified via email when they're approved or rejected.
            </p>
          </div>
        </div>
      </div>

      <div class="inline-flex justify-between">
        <div class="flex gap-spacing-sm flex-wrap">
          <el-select
            v-model="filters.status"
            style="width: 232px"
            placeholder="Status"
            clearable
            :disabled="isFetching"
          >
            <el-option
              v-for="status in Object.values(RepairRequestSubmissionStatus)"
              :key="status"
              :label="status"
              :value="status"
            />
          </el-select>
          <RcCustomerSelect
            v-if="isRcAppManagerUser"
            style="width: 232px"
            :disabled="isFetching"
            @change="handleCustomerSelectChange"
          />
        </div>
      </div>
      <el-table
        v-loading="isFetching"
        :data="data?.data"
        width="100%"
        :row-class-name="getRowClassName"
        class="[&_.repair-request-submissions-dashboard-table-row]:cursor-pointer"
        @row-click="handleOpen"
      >
        <el-table-column type="expand" width="50">
          <template #default="scope">
            <div class="p-4" style="background-color: var(--fill-secondary)">
              <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div v-if="scope.row.notes">
                  <div class="text-sm font-medium mb-2" style="color: var(--text-tertiary)">Notes</div>
                  <div
                    class="text-sm p-3 rounded border"
                    style="background-color: var(--fill-primary); border-color: var(--component-stroke-primary)"
                  >
                    {{ scope.row.notes }}
                  </div>
                </div>
                <div v-if="scope.row.reject_reason">
                  <div class="text-sm font-medium mb-2" style="color: var(--text-tertiary)">Rejection Reason</div>
                  <div
                    class="text-sm p-3 rounded border"
                    style="
                      background-color: var(--component-fill-orange);
                      border-color: var(--component-stroke-orange);
                      color: var(--text-badge-orange);
                    "
                  >
                    {{ scope.row.reject_reason }}
                  </div>
                </div>
                <div v-if="scope.row.repair_request?.work_order_number" class="col-span-full">
                  <div class="text-sm font-medium mb-2" style="color: var(--text-tertiary)">Work Order</div>
                  <div
                    class="text-sm p-3 rounded border"
                    style="background-color: var(--fill-primary); border-color: var(--component-stroke-primary)"
                  >
                    <el-button
                      link
                      class="inline-flex items-center gap-2 p-0"
                      @click="navigateToWorkOrder(scope.row.repair_request.work_order_number)"
                    >
                      {{ scope.row.repair_request.work_order_number }}
                      <Icon icon="material-symbols:open-in-new" height="14" width="14" />
                    </el-button>
                  </div>
                </div>
                <div
                  v-if="!scope.row.notes && !scope.row.reject_reason && !scope.row.repair_request?.work_order_number"
                  class="col-span-full text-center py-4"
                  style="color: var(--text-tertiary)"
                >
                  No additional data available
                </div>
              </div>
            </div>
          </template>
        </el-table-column>
        <el-table-column min-width="40" label="ID" prop="id" />
        <el-table-column v-if="!isCustomer" min-width="200" label="Customer">
          <template #default="scope">
            <div class="flex flex-col gap-1">
              <div class="font-medium">
                {{
                  scope.row.customer.company_name ?? `${scope.row.customer.first_name} ${scope.row.customer.last_name}`
                }}
              </div>
              <div class="text-xs text-gray-600">
                {{ scope.row.customer.email }}
              </div>
              <div v-if="scope.row.customer.phone" class="text-xs text-gray-600">
                {{ scope.row.customer.phone }}
              </div>
            </div>
          </template>
        </el-table-column>
        <el-table-column min-width="80" prop="expected_units" label="Equipment Quantity" />
        <el-table-column min-width="112" prop="scope_of_work" label="Requested Services">
          <template #default="scope">
            <div class="flex flex-wrap gap-1">
              <el-tag v-for="(item, index) in scope.row.scope_of_work" :key="index" size="small" type="info">
                {{ item.description }}
              </el-tag>
            </div>
          </template>
        </el-table-column>
        <el-table-column min-width="160" prop="miner_models" label="Equipment Models">
          <template #default="scope">
            <div class="flex flex-wrap gap-1">
              <el-tag v-for="(item, index) in scope.row.miner_models" :key="index" size="small" type="success">
                {{ item }}
              </el-tag>
            </div>
          </template>
        </el-table-column>
        <el-table-column min-width="88" prop="created_at" label="Submission Date" :formatter="dateFormatter" />
        <el-table-column min-width="104" label="Submission Status" align="center">
          <template #default="scope">
            <StatusBadge :status="scope.row.status" />
          </template>
        </el-table-column>
        <el-table-column min-width="104" label="Repair Request Status" align="center">
          <template #default="scope">
            <StatusBadge v-if="scope.row.repair_request" :status="scope.row.repair_request.status" />
          </template>
        </el-table-column>
        <el-table-column v-if="isRcAppManagerUser" :min-width="70" align="right">
          <template #default="scope">
            <el-button
              v-if="scope.row.status === RepairRequestSubmissionStatus.UnderReview"
              link
              title="Approve"
              @click="handleStatusApproved(scope.row.id)"
              ><Icon icon="material-symbols:check" height="16"
            /></el-button>
            <el-button
              v-if="scope.row.status === RepairRequestSubmissionStatus.UnderReview"
              link
              title="Reject"
              @click="handleStatusRejected(scope.row.id)"
              ><Icon icon="material-symbols:close" height="16"
            /></el-button>
            <el-button
              v-if="scope.row.status === RepairRequestSubmissionStatus.UnderReview"
              link
              title="Delete"
              @click="
                DeleteConfirmationModal(
                  'Delete Submission',
                  'Are you sure you want to delete this submission? This action cannot be undone',
                  scope.row.id,
                  deleteRepairRequestSubmissionMutation.mutateAsync
                )
              "
            >
              <Icon icon="material-symbols:delete" height="16" />
            </el-button>
          </template>
        </el-table-column>
      </el-table>
      <el-pagination
        v-if="data"
        v-model:current-page="filters.page"
        v-model:page-size="filters.per_page"
        class="self-end"
        :page-sizes="DEFAULT_PAGE_SIZES"
        :page-count="data.total_pages"
        :total="data.total_records"
        :disabled="isFetching"
        :layout="DEFAULT_PAGINATION_LAYOUT"
        background
      />
    </template>

    <!-- Repair Request Submission Dialog -->
    <RepairRequestSubmissionDialog
      v-if="createRepairRequestSubmissionDialogOpen"
      v-model="createRepairRequestSubmissionDialogOpen"
    />
  </ViewLayout>
</template>
